<template>
  <v-list-group
    v-if="item.children && isFavorites"
    :value="`${item.name}${item.path}`"
    class="item-group"
  >
    <template v-slot:activator="{ props }">
      <v-list-item
        v-bind="props"
        :prepend-icon="item.icon"
        :value="`${item.name}${item.path}`"
        :class="isCurrentRoute && 'current-route'"
      >
        <v-list-item-title>
          <span class="normal-wrap" v-html="highlightedTitle" />
        </v-list-item-title>
        <div class="item-tag" v-if="highlightedTag">
          (<span class="normal-wrap" v-html="highlightedTag" />)
        </div>
      </v-list-item>
    </template>
    <draggable
      v-model="favoritesComputed"
      item-key="favKey"
      :disabled="search?.length"
      class="favorites-list"
    >
    <template v-slot:item="{ element }">
      <SideBarList
        :key="element.favKey"
        :item="element"
        :search="search"
        :isFavorites="isFavorites"
      />
    </template>
    </draggable>
  </v-list-group>
  <v-list-group
    v-else-if="item.children"
    :value="`${item.name}${item.path}`"
    class="item-group hierarchy-line"
  >
    <template v-slot:activator="{ props }">
      <v-list-item
        v-if="childrenShowItem"
        v-bind="props"
        :prepend-icon="item.icon"
        :key="item.name"
        :value="`${item.name}${item.path}`"
        :class="isCurrentRoute && 'current-route'"
      >
        <v-list-item-title>
          <span class="normal-wrap" v-html="highlightedTitle" />
        </v-list-item-title>
        <div class="item-tag" v-if="highlightedTag">
          (<span class="normal-wrap" v-html="highlightedTag" />)
        </div>
      </v-list-item>
    </template>
    <SideBarList
      v-for="route in item.children"
      :key="route.path"
      :item="route"
      :search="search"
      :level="level + 1"
    />
  </v-list-group>
  <v-list-item
    v-else-if="showItem"
    nav
    :key="`${item.path}${item.meta?.id}`"
    @click="navigate"
    :class="isCurrentRoute && 'current-route'"
    :style="isFavorites && 'margin: 0px'"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp"
    @touchstart="onMouseDown"
    @touchend="onMouseUp"
  >
    <v-list-item-title class="isFavorites-item">
      <div :class="isCurrentRoute && !isFavorites && 'current-route-pill'" class="d-flex align-center">
        <div style="width: 40px" v-if="item.icon">
          <v-icon v-if="!item.loading">{{ item.icon }}</v-icon>
          <v-progress-circular
            v-else-if="item.loading"
            :size="25"
            indeterminate
            class="mr-4"
          />
        </div>
        <span
          class="normal-wrap"
          v-html="highlightedTitle"
        />
      </div>
      <v-icon v-if="isFavorites">mdi-drag</v-icon>
    </v-list-item-title>
    <div class="item-tag" v-if="highlightedTag">
      (<span class="normal-wrap" v-html="highlightedTag" />)
    </div>
  </v-list-item>
</template>
<script setup>
import { toRefs, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import draggable from 'vuedraggable'
import { useRootStore } from "../stores/root";

const props = defineProps({
  item: {
    type: Object,
  },
  search: {
    type: String,
  },
  isFavorites: {
    type: Boolean,
    default: false,
  },
  level: {
    type: Number,
    default: 0,
  },
});
const { item, search, level, isFavorites } = toRefs(props);

const route = useRoute();
const router = useRouter();
const currentRoute = computed(() => route.path);

const highlightedTitle = computed(() => {
  let title = item.value.name;
  if (search.value) {
    const regex = new RegExp(search.value, 'gi');
    title = item.value.name.replace(regex, (match) => `<b class="highlight-search">${match}</b>`);
  }
  if (item.value.test) title += ' *';
  return title;
})

const highlightedTag = computed(() => {
  if (!item.value.tags) return false;
  if (!search.value) return false;
  const regex = new RegExp(search.value, 'gi');
  const tags = item.value.tags.reduce((acc, tag) => {
    const matches = tag.match(regex);
    if (matches) {
      acc.push({
        matches: matches.length,
        tag: tag.replace(regex, (match) => `<b class="highlight-search">${match}</b>`),
      });
    }
    return acc;
  }, []);
  if (!tags.length) return false;
  return tags.sort((a, b) => b.matches - a.matches)[0].tag;
})

function addBaseToRoute(path) {
  // strip the last / from the base url, so /admin/ => /admin, /admin => /admin, / => /
  const stripped = import.meta.env.BASE_URL.replace(/\/$/, '');
  return import.meta.env.BASE_URL === '/' ? path : `${stripped}${path}`;
}

const allHomePages = [
  '/',
  '/portal/feed',
  '/feed',
  '/home-dashboard',
  '/reports/mcd/dashboardtest'
];

function isHomePage(path) {
  for (const homePage of allHomePages) {
    if (path === homePage) return true;
  }
  return false;
}

const isCurrentRoute = computed(() => {
  if (allHomePages.includes(item.value.path) && isHomePage(addBaseToRoute(currentRoute.value))) {
    return true;
  }
  // if we have children, check those
  function isChildCurrentRoute(routes) {
    if (routes.children) return routes.children.find(isChildCurrentRoute);
    if (routes.menuPath === addBaseToRoute(currentRoute.value)) return true;
    return false;
  }
  if (item.value.children) return item.value.children.find(isChildCurrentRoute);
  return item.value.menuPath === addBaseToRoute(currentRoute.value);
})

function navigate() {
  if (item.value.onClick) {
    item.value.onClick();
  } else if (item.value.path) {
    router.push(item.value.path);
  }
}

const childrenShowItem = computed(() => {
  if (!item.value.children) return true;
  // get the root children
  function reducer(acc, child) {
    if (!child.children) acc.push(child);
    else acc.push(...child.children.reduce(reducer, []));
    return acc;
  }
  const rootChildren = item.value.children.reduce(reducer, []);
  return rootChildren.some(x => x.showItem ? x.showItem.value : true);
  // return item.value.children.some(x => x.showItem ? x.showItem.value : true);
})

const showItem = computed(() => {
  if (item.value.showItem) return item.value.showItem.value;
  return true;
});

const store = useRootStore();

const favoritesComputed = computed({
  get() {
    return item.value.children.map(x => ({
      ...x,
      favKey: x.meta.id,
    }))
  },
  set(v) {
    const favorites = v.map(x => x.meta.id);
    store.setFavoritePages(favorites);
    // item.value.children = v;
  },
})

const padding = computed(() => level.value * 36 + 20);
const paddingPx = computed(() => {
  return `${padding.value}px`;
});

// handle mobile clicking to not confuse click vs drag
let mouseDownTime = 0;
function onMouseDown() {
  if (!isFavorites.value) return;
  mouseDownTime = Date.now();
}
function onMouseUp(event) {
  if (!isFavorites.value) return;
  const clickDuration = Date.now() - mouseDownTime;
  if (clickDuration < 300) {
    navigate();
    event.preventDefault();
  }
}
</script>

<style scoped lang="scss">
.normal-wrap {
  white-space: normal;
}
.item-tag {
  font-size: 0.75em;
  max-width: 150px;
}
.current-route {
  color: rgb(var(--v-theme-primary));
  font-weight: bold;
}
.current-route-pill {
  background: rgb(var(--v-theme-surface-alt-medium));
  color: rgb(var(--v-theme-primary));
  padding: 8px;
  border-radius: 8px;
}
:deep(.highlight-search) {
  color: rgb(var(--v-theme-primary));
  font-weight: bold;
  text-decoration: underline;
}
.item-group {
  // padding-inline-start: 10px;
  --prepend-width: 20px !important;
}
.favorites-list {
  border-bottom: 1px solid rgb(var(--v-theme-border-low));
}
.isFavorites-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.hierarchy-line {
  position: relative;
  :deep(.v-list-group__items) {
    position: relative;
    &::before {
      content: '';
      position: absolute;
      left: v-bind(paddingPx);
      top: 0;
      bottom: 0;
      width: 1px;
      background-color: rgb(var(--v-theme-border-low));
      // border-left: 1px solid rgb(var(--v-theme-on-surface));
    }
  }
}
</style>